import React, { useState, useEffect } from "react";
import { useApi } from "../hooks/useApi";
import { AlertTriangle } from "lucide-react";

const formatImpact = (val) => {
    if (val == null) return "\u2014";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    return `$${Math.round(n).toLocaleString()}`;
};

const formatPercent = (val) => {
    if (val == null) return "\u2014";
    return `${(Number(val) * 100).toFixed(1)}%`;
};

const VendorAnomalyBadge = ({ vendorId, onAnomalyData }) => {
    const apiCall = useApi();
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const result = await apiCall("GET", `/TPRMVendor/${vendorId}/anomaly-check?threshold=5`);
                if (!cancelled) {
                    setData(result);
                    if (onAnomalyData) onAnomalyData(result);
                }
            } catch {
                // silently ignore — badge just won't render
            }
        };
        load();
        return () => { cancelled = true; };
    }, [vendorId, apiCall, onAnomalyData]);

    if (!data?.anomalyDetected) return null;

    const deltaPercent = data.aviDeltaPercent != null ? Math.abs(data.aviDeltaPercent) : null;
    const isRed = deltaPercent != null && deltaPercent >= 15;
    const badgeColor = isRed
        ? "bg-red-100 text-red-800 border-red-300"
        : "bg-amber-100 text-amber-800 border-amber-300";

    return (
        <div className="relative inline-block">
            <button
                onClick={() => setExpanded(!expanded)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${badgeColor} animate-pulse cursor-pointer`}
            >
                <AlertTriangle className="h-3 w-3" />
                Anomaly {deltaPercent != null ? `(${deltaPercent.toFixed(0)}%)` : ""}
            </button>

            {expanded && (
                <div className="absolute z-20 top-full mt-2 left-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Anomaly Details</h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Current AVI:</span>
                            <span className="font-medium">{formatImpact(data.currentAvi)}{data.currentAvi != null ? '+' : ''}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Previous AVI:</span>
                            <span className="font-medium">{formatImpact(data.previousAvi)}{data.previousAvi != null ? '+' : ''}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Current RPI:</span>
                            <span className="font-medium">{formatPercent(data.currentRpi)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Previous RPI:</span>
                            <span className="font-medium">{formatPercent(data.previousRpi)}</span>
                        </div>
                        {data.currentSnapshotDate && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Current Snapshot:</span>
                                <span className="font-medium">{new Date(data.currentSnapshotDate).toLocaleDateString()}</span>
                            </div>
                        )}
                        {data.previousSnapshotDate && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Previous Snapshot:</span>
                                <span className="font-medium">{new Date(data.previousSnapshotDate).toLocaleDateString()}</span>
                            </div>
                        )}
                        {data.explanation && (
                            <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                                <div className="text-xs font-semibold text-gray-600 mb-1">AI Explanation</div>
                                <p className="text-xs text-gray-700">{data.explanation}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorAnomalyBadge;
